import React, { useEffect, useRef, useState } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';

type TimecodeInputProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value?: string;
  onChange?: (value: string) => void;
};

const onlyDigits = (s: string) => s.replace(/\D+/g, '');

const formatDigitsToTime = (digits: string) => {
  const d = digits.slice(0, 9);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}:${d.slice(2)}`;
  if (d.length <= 6) return `${d.slice(0, 2)}:${d.slice(2, 4)}:${d.slice(4)}`;
  return `${d.slice(0, 2)}:${d.slice(2, 4)}:${d.slice(4, 6)}:${d.slice(6)}`;
};

export const parseTimecodeToSeconds = (value: string): number => {
  const digits = (value || '').replace(/\D+/g, '');
  if (!digits) return 0;
  const padded = digits.padEnd(9, '0').slice(0, 9);
  const hh = parseInt(padded.slice(0, 2), 10) || 0;
  const mm = parseInt(padded.slice(2, 4), 10) || 0;
  const ss = parseInt(padded.slice(4, 6), 10) || 0;
  const ms = parseInt(padded.slice(6, 9), 10) || 0;
  return hh * 3600 + mm * 60 + ss + ms / 1000;
};

export const formatSecondsToTimecode = (seconds: number): string => {
  const totalMs = Math.max(0, Math.round((seconds || 0) * 1000));
  const hh = Math.floor(totalMs / 3600000);
  const mm = Math.floor((totalMs % 3600000) / 60000);
  const ss = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const pad3 = (n: number) => String(n).padStart(3, '0');
  return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}:${pad3(ms)}`;
};

const TimecodeInput: React.FC<TimecodeInputProps> = ({ value: valueProp = '', onChange, ...tfProps }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(() => formatDigitsToTime(onlyDigits(valueProp)));

  useEffect(() => {
    setValue(formatDigitsToTime(onlyDigits(valueProp)));
  }, [valueProp]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const el = e.target as HTMLInputElement;
    const raw = el.value;
    const digits = onlyDigits(raw).slice(0, 9);
    const formatted = formatDigitsToTime(digits);

    const prev = value;
    const prevDigits = onlyDigits(prev);
    const added = digits.length > prevDigits.length;

    setValue(formatted);
    onChange?.(formatted);

    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      try {
        const pos = input.selectionStart ?? formatted.length;
        if (added) {
          if (pos === 2 || pos === 5 || pos === 8) {
            input.setSelectionRange(pos + 1, pos + 1);
            return;
          }
        }
        input.setSelectionRange(pos, pos);
      } catch (err) {
        console.error(err);
      }
    });
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const digits = onlyDigits(value);
    if (digits.length === 0) return;
    const padded = digits.padEnd(9, '0');
    const formatted = formatDigitsToTime(padded);
    if (formatted !== value) {
      setValue(formatted);
      onChange?.(formatted);
    }
    tfProps.onBlur?.(e as any);
  };

  return (
    <TextField
      {...tfProps}
      inputRef={inputRef}
      fullWidth
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="HH:mm:ss:ms"
    />
  );
};

export default TimecodeInput;
